import { Icon } from '@blueprintjs/core';
import * as React from 'react';
import { Entity, Field } from 'typegraph';

import { ID } from 'common/entities/IEntity';
import Loading from 'common/fragments/Loading';
import { BlockView } from 'common/views/block/BlockView.view';
import { OptionDropdownSelect } from 'common/views/dropdownSelect/OptionDropdownSelect';
import MenuTreeView, { IMenuNode } from 'common/views/menuTree/MenuTree.view';
import { TagView } from 'common/views/tag/TagView.view';
import Query from 'core/api/QueryComponent';
import Label from 'core/components/label/Label';
import { IPanelProps, Panel } from 'core/decorators/Panel';
import StringUtil from 'core/util/StringUtil';
import RefineTagPanelConfig from './RefineTagPanelConfig';

/**
 * @overview: Panel for pushing search and sort query params used
 * by entry list / catalog panels
 *
 * @author: jesposito, chills
 */

interface IRefineTagPanelState {
	expanded: { [id: string]: boolean; };
}

interface ITagData {
	id: ID;
	name: string;
}

const PARAM_TAGS = "tagIds";

@Entity({many: 'tags'})
class Tag {

	@Field()
	public id: ID;

	@Field()
	public name: string;

	@Field()
	public sortIndex: number;

	@Field()
	public parentId: ID;

}

@Entity({many: "tagSets"})
class TagSet {

	@Field()
	public id: ID;

	@Field()
	public name: string;

	@Field()
	public sortIndex: number;

	@Field({entity: Tag, params: {params: {pageSize: 1000}}})
	public tags: null | Tag[];

}

@Panel({id: "refine-tag", configClass: RefineTagPanelConfig, pageParams: true})
export class RefineTagPanel extends React.PureComponent<IPanelProps<RefineTagPanelConfig>, IRefineTagPanelState> {

	public state = { expanded: {} };


	public render() {
		const { config, pageParams } = this.props;

		const params = {
			ids: config.tagSets && config.tagSets.length > 0 && config.tagSets.map((id) => StringUtil.toInteger(id)) || null,
			pageSize: 100
		};

		if (config.target === 'article') {
			params['entityTypeNames'] = ['com.digitalignite.module.kb.entity.Article'];
		}

		if (config.target === 'item') {
			params['entityTypeNames'] = ['com.digitalignite.module.catalog.entity.Item'];
		}

		const activeTagIds: ID[] = pageParams[PARAM_TAGS] ? pageParams[PARAM_TAGS].split(',') : [];

		return [
			config.showTagConditionControl && activeTagIds.length > 1 && (
				<OptionDropdownSelect
					viewKey="tag.condition"
					options={[
						{value: "AND", label: "ALL"},
						{value: "OR", label: "ANY"}
					]}
					value={pageParams['tagCondition'] === 'OR' ? 'OR' : 'AND'}
					onChange={this.onChangeTagCondition}
				>
					<Label k="tag.condition" v="Show matches with" />
				</OptionDropdownSelect>
			),
			config.showActiveTags && activeTagIds.length > 0 && (
				<Query.Many entity={Tag} args={{ids: activeTagIds}} key="selected">
					{
						(args) => {
							if (args.loading || !args.data || args.data.length < 1) {
								return null;
							}

							return (
								<BlockView viewKey="selected">
									{args.data.map((tag) => {

										return (
											<TagView
												viewKey="tag.selected"
												key={tag.id}
												value={tag.id}
												onRemove={this.onRemoveTag}
												style={{margin: "0 7px 7px 0"}}
											>
												<Label k={`Tag.${tag.id}.name`} v={tag.name} />
											</TagView>
										);
									})}
								</BlockView>
							);
						}
					}
				</Query.Many>
			),
			<Query.Many entity={TagSet} args={params} key="tagSets">
				{
					(args) => {

						if (args.loading) {
							return <Loading />;
						}

						if (!args.data || args.data.length < 1) {
							return null;
						}

						let tagSets: TagSet[] = args.data;

						// force panel-specific sort order
						if (config.sortTagSets && params.ids && params.ids.length > 0) {
							tagSets = params.ids.reduce((lst, id) => {

								const tagSet = tagSets.find((ts) => (ts.id).toString() === (id).toString());

								if (tagSet) {
									lst.push(tagSet);
								}

								return lst;
							}, []);
						}

						const items = tagSets.map<IMenuNode>((ts) => {
							const id = 'tagSet-' + ts.id;
							return {
								id,
								label: <Label k={`TagSet.${ts.id}.name`} v={ts.name} />,
								isSelectable: false,
								isExpanded: !!this.state.expanded[id],
								children: this.mapTagsToNodes(ts.tags)
							};
						});

						return (
							<MenuTreeView
								viewKey="menu"
								items={items}
								onExpandedStateChange={this.onExpandedStateChange}
								onSelectItem={this.onSelectTag}
							/>
						);
					}
				}
			</Query.Many>
		];
	}

	private mapTagsToNodes = (tags: Tag[], parentId?: ID): null | IMenuNode[] => {

		return (tags || []).reduce<IMenuNode[]>((lst, tag) => {

			if (
				(!parentId && !tag.parentId) ||
				(parentId && tag.parentId && (tag.parentId).toString() === (parentId).toString())
			) {
				const id = 'tag-' + tag.id;

				const isActive = this.isSelected(tag);

				lst.push({
					id,
					label: <Label k={`Tag.${tag.id}.name`} v={tag.name} />,
					data: tag.id,
					children: this.mapTagsToNodes(tags, tag.id),
					isExpanded: !!this.state.expanded[id],
					isActive,
					icon: isActive ? <Icon icon="small-tick" /> : undefined
				});
			}

			return lst;
		}, []);
	}

	private onExpandedStateChange = (node: IMenuNode, isExpanded: boolean) => {
		this.setState({
			expanded: {...this.state.expanded, [node.id]: isExpanded}
		});
	}

	private isSelected = (tag: Tag) => !!this.props.pageParams[PARAM_TAGS] && this.props.pageParams[PARAM_TAGS].split(',').indexOf(tag.id.toString()) > -1;

	private getActiveTagIds = (): string[] => {
		return this.props.pageParams[PARAM_TAGS] && this.props.pageParams[PARAM_TAGS].split(',') || [];
	}

	private onChangeTagCondition = (condition: "AND" | "OR") => {
		this.props.actions.pushPageParam("tagCondition", condition);
	}

	private onSelectTag = (node: IMenuNode<ID>) => {
		const { actions, config, pageParams } = this.props;

		const selected: string[] = pageParams[PARAM_TAGS] && pageParams[PARAM_TAGS].split(',') || [];

		const tagId = node.data;

		let nextValue: any;

		if (!selected) {

			// 1. if nothing selected, always add
			nextValue = tagId;

		} else if (selected.length === 1 && selected[0] === tagId) {

			// 2. if only this one selected, remove the param
			nextValue = "";

		} else {

			// 3. if some already selected, update based on config logic

			if (config.tagSelection === 'one') {

				// only one tag at a time
				nextValue = tagId;

			} else if (config.tagSelection === 'onPerSet') {

				// one tag from each tag set at a time

			} else {

				// multiple from each tag set
				nextValue = (selected.includes(tagId) ? selected.filter((id) => id !== tagId) : [...selected, tagId]).join(',');
			}
		}

		// always reset page number when this changes
		actions.pushPageParams({key: PARAM_TAGS, value: nextValue}, {key: 'pageNumber', value: 0});
	}

	private onRemoveTag = (id: ID) => {
		this.props.actions.pushPageParam(PARAM_TAGS, this.getActiveTagIds().filter((tid) => tid !== (id).toString()).join(','));
	}
}



// WEBPACK FOOTER //
// ./src/panels/refineTag/RefineTag.panel.tsx